import { z } from 'zod';
import { userRoleSchema } from '../constants/roles.js';
import { shopDaySchema, shopDayStart } from '../reports/date-ranges.js';
import {
  dateInputSchema,
  emailSchema,
  isoDateStringSchema,
  objectIdSchema,
  paginationQuerySchema,
  pinSchema,
} from './common.js';

/**
 * Who was in the shop today, when they arrived, when they left, and who is on leave.
 *
 * ONE RECORD PER STAFF MEMBER PER CALENDAR DAY. That rule is what the rest of the module rests
 * on: a second check-in on the same day edits the same row rather than adding another one, so
 * "who worked today" is a single read that can never double-count the tailor who stepped out at
 * lunch and tapped the kiosk again on her way back in. `(staffUserId, day)` is unique inside a
 * shop, and every write below is an upsert against that pair.
 *
 * THE DAY IS A SHOP-TIMEZONE `YYYY-MM-DD` STRING, not a timestamp. A shop that closes at 11pm
 * files that evening's work under that evening's date; a UTC instant would file it under
 * tomorrow and split one working day across two rows. The arrival and departure stay instants,
 * because "when exactly did she get in" is a moment, not a day.
 *
 * WHAT THE SERVER DECIDES, A CLIENT NEVER SENDS. `workedMinutes`, `late` and `halfDay` are
 * computed from the two instants against the shop's own `graceMinutes` and
 * `halfDayThresholdMinutes`, and `source` is derived from who is calling. None of the four
 * appears in any request schema in this file — a client that could set them could also decide
 * it had never been late.
 */

/* -------------------------------------------------------------------------------------------
 * Status and source
 * ---------------------------------------------------------------------------------------- */

/**
 * Where one person's day has got to.
 *
 * `not_started` is the state of a row nobody has touched yet — the shop's roster read through
 * today's date — and is what the attendance screen shows against everybody at opening time.
 * `working` is checked in and not yet out, `completed` is both. `on_leave` is the one value
 * that is always written by a person rather than reached by the clock.
 */
export const ATTENDANCE_STATUSES = ['not_started', 'working', 'completed', 'on_leave'] as const;
export type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number];
export const attendanceStatusSchema = z.enum(ATTENDANCE_STATUSES);

/**
 * Who put the row there — `manager` when somebody with authority recorded it for another
 * person, `self` when the staff member recorded their own.
 *
 * Two values are enough, and deliberately so: a public kiosk check-in is the staff member
 * acting for themselves, so it records `self` and the enum does not have to grow a third value
 * when the kiosk link lands. What differs between a kiosk and a phone is the credential, not
 * whose attendance it is.
 */
export const ATTENDANCE_SOURCES = ['manager', 'self'] as const;
export type AttendanceSource = (typeof ATTENDANCE_SOURCES)[number];
export const attendanceSourceSchema = z.enum(ATTENDANCE_SOURCES);

/** Long enough for "at her sister's wedding in Erode", short enough to stay one line on a card. */
export const ATTENDANCE_LEAVE_REASON_MAX_LENGTH = 200;

/* -------------------------------------------------------------------------------------------
 * The record
 * ---------------------------------------------------------------------------------------- */

export const attendanceRecordSchema = z.object({
  id: objectIdSchema,

  /**
   * Who worked. The name, email and role are denormalised alongside the id exactly as a
   * quotation denormalises the lead it hangs off — a row renders without a second lookup, and
   * the roster of forty people behind today's list stays one query rather than forty-one.
   */
  staffUserId: objectIdSchema,
  staffEmail: z.string(),
  staffName: z.string(),
  staffRole: userRoleSchema,

  /** Shop-timezone `YYYY-MM-DD`. Unique with `staffUserId` inside a shop. */
  day: shopDaySchema,

  /** Null until somebody arrives, and null on a day taken as leave. */
  checkInAt: isoDateStringSchema.nullable(),
  /** Null while they are still here — an arrival without a departure is the normal afternoon. */
  checkOutAt: isoDateStringSchema.nullable(),

  /** Server-computed from the two instants. Never sent by a client. */
  workedMinutes: z.number().int().nonnegative(),

  status: attendanceStatusSchema,

  /** Server-computed against the shop's grace and half-day settings. Never sent by a client. */
  late: z.boolean(),
  /**
   * HOW late, in whole minutes past the grace period. Zero when they were on time and zero when
   * they never arrived — an absence is not lateness, and counting a missing arrival as an
   * enormous number of late minutes would poison every total it appeared in.
   *
   * Derived like `late` is, and against the same threshold, so the two can never disagree. What
   * it adds is the thing a shop actually argues about: "late three times" and "late by four
   * hours in total" are different conversations and only one of them was answerable before.
   */
  lateMinutes: z.number().int().nonnegative(),
  halfDay: z.boolean(),
  /**
   * The hours this person was measured against — their own shift when they have one, the
   * shop's workday when they do not. Carried on the row so the screen can say WHY somebody is
   * marked late at 10:20 when the shop opens at ten and she starts at nine.
   */
  shiftStart: z.string(),
  shiftEnd: z.string(),
  /** True when the two above came from the person's own shift rather than the shop's hours. */
  personalShift: z.boolean(),

  /** Only ever set on an `on_leave` day. Null everywhere else. */
  leaveReason: z.string().nullable(),

  source: attendanceSourceSchema,
  /** Null when the person recorded their own day — the staff id above already says who. */
  recordedByUserId: objectIdSchema.nullable(),
  recordedByName: z.string().nullable(),

  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type AttendanceRecord = z.infer<typeof attendanceRecordSchema>;

/* -------------------------------------------------------------------------------------------
 * Settings
 * ---------------------------------------------------------------------------------------- */

/** A 24-hour `HH:MM` clock time — what `<input type="time">` produces. */
const workdayTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a 24-hour HH:MM time');

const attendanceSettingsObject = z
  .object({
    /** When the shop expects people in. `late` is measured from here plus the grace below. */
    workdayStart: workdayTimeSchema,
    workdayEnd: workdayTimeSchema,
    /**
     * Minutes after `workdayStart` that are not counted as late.
     *
     * This setting is the only thing that gives the `late` flag a meaning, and shops disagree
     * about it by a lot — a boutique that opens at ten does not consider five past ten late.
     */
    graceMinutes: z.number().int().min(0).max(180),
    /** Below this many worked minutes a present day is a half day rather than a full one. */
    halfDayThresholdMinutes: z
      .number()
      .int()
      .min(30)
      .max(12 * 60),
    /**
     * Whether staff may record their own arrival at all. Off by default: a shop that has never
     * opened this screen should not already be accepting attendance nobody supervised.
     */
    selfCheckInEnabled: z.boolean(),
  })
  .strict();

/**
 * Per-shop attendance settings.
 *
 * A workday must END AFTER IT STARTS. That refuses an overnight shift written as 22:00–06:00,
 * which is deliberate: this module counts one calendar day per person, so a shift that crosses
 * midnight has no honest row to live in and would silently produce negative worked minutes.
 */
export const attendanceSettingsSchema = attendanceSettingsObject.refine(
  (value) => value.workdayEnd > value.workdayStart,
  { message: 'The workday must end after it starts', path: ['workdayEnd'] },
);
export type AttendanceSettings = z.infer<typeof attendanceSettingsSchema>;

/**
 * What a shop that has never opened the screen gets.
 *
 * Exported so the seeder on the server and the settings form in the browser start from the same
 * numbers rather than each inventing their own — two sets of defaults is how a shop ends up
 * with a `late` flag that disagrees with the form it was never shown.
 */
export const DEFAULT_ATTENDANCE_SETTINGS: AttendanceSettings = Object.freeze({
  workdayStart: '10:00',
  workdayEnd: '20:00',
  graceMinutes: 15,
  halfDayThresholdMinutes: 240,
  selfCheckInEnabled: false,
});

/** Every answer optional: the settings screen saves one field at a time. */
export const updateAttendanceSettingsSchema = attendanceSettingsObject
  .partial()
  .strict()
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' })
  .refine(
    (patch) =>
      patch.workdayStart === undefined ||
      patch.workdayEnd === undefined ||
      patch.workdayEnd > patch.workdayStart,
    { message: 'The workday must end after it starts', path: ['workdayEnd'] },
  );
export type UpdateAttendanceSettingsInput = z.input<typeof updateAttendanceSettingsSchema>;

/* -------------------------------------------------------------------------------------------
 * The day search
 * ---------------------------------------------------------------------------------------- */

export const attendanceSearchQuerySchema = paginationQuerySchema.extend({
  /** One calendar day in the shop's timezone. Omitted means the server decides today. */
  day: shopDaySchema.optional(),
  /** Repeat the parameter for several, as the quotation search does. Omitted means everybody. */
  role: z
    .union([userRoleSchema, z.array(userRoleSchema)])
    .transform((value) => (Array.isArray(value) ? value : [value]))
    .optional(),
  status: attendanceStatusSchema.optional(),
  /** Free text over a staff member's name or email address. */
  q: z.string().trim().max(120).optional(),
});
export type AttendanceSearchQuery = z.infer<typeof attendanceSearchQuerySchema>;

/**
 * The four tiles at the top of the attendance screen.
 *
 * THESE COUNT THE WHOLE DAY, NOT THE PAGE. A shop with forty staff looking at page 2 of 25 must
 * still be told it has forty — a tile that agreed with the page under it would report "15 staff"
 * and be wrong in the one place an owner glances at without reading.
 */
export const attendanceSummarySchema = z.object({
  totalStaff: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
  working: z.number().int().nonnegative(),
  onLeave: z.number().int().nonnegative(),
});
export type AttendanceSummary = z.infer<typeof attendanceSummarySchema>;

export const attendancePageSchema = z.object({
  items: z.array(attendanceRecordSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  /** Day-wide and NOT page-scoped — see {@link attendanceSummarySchema}. */
  summary: attendanceSummarySchema,
});
export type AttendancePage = z.infer<typeof attendancePageSchema>;

/* -------------------------------------------------------------------------------------------
 * Requests
 * ---------------------------------------------------------------------------------------- */

/**
 * Arriving. The day is not in the body because a check-in is always today's — the server reads
 * the shop's calendar rather than trusting a client's clock about which day it is.
 *
 * `at` exists for the manager who is recording an arrival twenty minutes after it happened.
 * Omitted means now.
 */
export const checkInSchema = z.object({
  staffUserId: objectIdSchema,
  at: dateInputSchema.optional(),
});
export type CheckInInput = z.input<typeof checkInSchema>;

/** Leaving. Same shape, same reasoning. */
export const checkOutSchema = z.object({
  staffUserId: objectIdSchema,
  at: dateInputSchema.optional(),
});
export type CheckOutInput = z.input<typeof checkOutSchema>;

/**
 * A manager writing or correcting one day — the upsert on `(staffUserId, day)`.
 *
 * The day is REQUIRED here, unlike a check-in: this is the schema that marks somebody on leave
 * next Tuesday and the one that fixes the Thursday a tailor forgot to tap out on, so it can
 * never mean "today" by default.
 *
 * `source` is absent on purpose — the server derives it from who is calling — as are
 * `workedMinutes`, `late` and `halfDay`, which it computes.
 */
export const recordAttendanceSchema = z
  .object({
    staffUserId: objectIdSchema,
    day: shopDaySchema,
    status: attendanceStatusSchema,
    /** `dateInputSchema`, so a form may send `2026-08-16T09:30` from `<input type="datetime-local">`. */
    checkInAt: dateInputSchema.nullish(),
    checkOutAt: dateInputSchema.nullish(),
    leaveReason: z.string().trim().max(ATTENDANCE_LEAVE_REASON_MAX_LENGTH).nullish(),
  })
  .refine(
    (value) =>
      !value.checkInAt ||
      !value.checkOutAt ||
      value.checkOutAt.getTime() >= value.checkInAt.getTime(),
    { message: 'The time out must not be before the time in', path: ['checkOutAt'] },
  );
export type RecordAttendanceInput = z.input<typeof recordAttendanceSchema>;

/* -------------------------------------------------------------------------------------------
 * One person's history
 * ---------------------------------------------------------------------------------------- */

/**
 * The longest history one request may ask for: a shop-year.
 *
 * The day search answers "who is in today" and cannot answer "what has this person's attendance
 * looked like", which is the question a wage conversation actually starts from. Capping the span
 * keeps that question from becoming a walk over every row the shop has ever written.
 */
export const MAX_ATTENDANCE_HISTORY_DAYS = 366;

/** One person over a date range — the day search cannot express this. */
export const attendanceHistoryQuerySchema = paginationQuerySchema
  .extend({
    staffUserId: objectIdSchema,
    from: shopDaySchema,
    to: shopDaySchema,
  })
  .superRefine((value, ctx) => {
    if (value.from > value.to) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'The start date must not be after the end date',
        path: ['from'],
      });
      return;
    }
    const spanDays =
      Math.round(
        (shopDayStart(value.to).getTime() - shopDayStart(value.from).getTime()) / 86_400_000,
      ) + 1;
    if (spanDays > MAX_ATTENDANCE_HISTORY_DAYS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Choose a shorter period — attendance history covers at most a year at a time',
        path: ['to'],
      });
    }
  });
export type AttendanceHistoryQuery = z.infer<typeof attendanceHistoryQuerySchema>;

/**
 * The totals under a history. ACROSS THE WHOLE RANGE, not the page — the same rule the day
 * search's summary follows, for the same reason: page 2 of a fortnight must not report a
 * fortnight's pay as five days of it.
 */
export const attendanceHistoryTotalsSchema = z.object({
  workedMinutes: z.number().int().nonnegative(),
  /** Days with an arrival recorded, however short. */
  present: z.number().int().nonnegative(),
  late: z.number().int().nonnegative(),
  /** The other half of the lateness question: not how often, but by how much in total. */
  lateMinutes: z.number().int().nonnegative(),
  halfDay: z.number().int().nonnegative(),
  onLeave: z.number().int().nonnegative(),
  /**
   * From the HR ledger rather than from the attendance rows — the two are counted together
   * here because a wage conversation asks about them together, and making the screen fetch
   * them separately is how the two figures end up covering different date ranges.
   */
  overtimeMinutes: z.number().int().nonnegative(),
  permissionMinutes: z.number().int().nonnegative(),
});
export type AttendanceHistoryTotals = z.infer<typeof attendanceHistoryTotalsSchema>;

export const attendanceHistoryPageSchema = z.object({
  items: z.array(attendanceRecordSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  /** Range-wide and NOT page-scoped — see {@link attendanceHistoryTotalsSchema}. */
  totals: attendanceHistoryTotalsSchema,
});
export type AttendanceHistoryPage = z.infer<typeof attendanceHistoryPageSchema>;

/* -------------------------------------------------------------------------------------------
 * The attendance kiosk link — an unauthenticated page whose token IS the credential
 * ---------------------------------------------------------------------------------------- */

/**
 * A shop-wide link an owner shares with staff: "copy and share this link with employees". A
 * staff member opens it on their own phone, or on a tablet by the shop door, types the email
 * and PIN they already sign in with, and taps Check in or Check out. No account session, no
 * app, no navigation — the token in the URL is the whole credential, and every way the link can
 * fail to resolve earns the one identical refusal, so a forged token cannot be told apart from a
 * revoked one.
 *
 * `kioskCheckInSchema` is `.strict()` and deliberately narrow: `email`, `pin` and `action` are
 * the only three things a staff member at the door can tell the server. The day, the staff id
 * and the instant are all things the SERVER decides — from the verified PIN, from the shop's
 * own calendar, and from the clock — never from the body, so a `.strict()` schema is what turns
 * a client that tried to send `staffUserId` or `day` into a 400 rather than a silently ignored
 * field.
 */

/** 32 random bytes in base64url is exactly 43 unpadded characters. Minted with
 * `randomBytes(32).toString('base64url')`. */
export const kioskTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'Not a kiosk token');

/** The path a kiosk link is served from. Short, because it is typed off a phone screen. */
export const KIOSK_PATH_PREFIX = '/k/';

/** The link an owner copies and shares. `baseUrl` is the SPA origin (`PUBLIC_APP_URL`). */
export function kioskLinkUrl(baseUrl: string, token: string): string {
  return `${baseUrl.replace(/\/+$/, '')}${KIOSK_PATH_PREFIX}${token}`;
}

/** The two things a staff member can do at the door. */
export const kioskActionSchema = z.enum(['check_in', 'check_out']);
export type KioskAction = z.infer<typeof kioskActionSchema>;

/**
 * What the door tablet sends. Exactly these three fields — `.strict()` refuses a body that
 * additionally carries a `staffUserId`, a `day`, an `at` or a `source` rather than quietly
 * dropping them, because a client that COULD set any of those could also decide it had never
 * been late.
 */
export const kioskCheckInSchema = z
  .object({
    email: emailSchema,
    pin: pinSchema,
    action: kioskActionSchema,
  })
  .strict();
export type KioskCheckInInput = z.infer<typeof kioskCheckInSchema>;

/**
 * What a SUCCESSFUL kiosk request returns — the complete allowlist of what this public,
 * unauthenticated endpoint may ever disclose. Nothing about the shop, nothing about any other
 * staff member, and no ids: the response says who just checked in, what was recorded and when,
 * and nothing that would let the endpoint be used to learn who else works here.
 */
export const kioskResultSchema = z.object({
  staffName: z.string(),
  action: kioskActionSchema,
  /** The instant this action was recorded — `checkInAt` for a check-in, `checkOutAt` for a
   * check-out. */
  at: isoDateStringSchema,
  checkInAt: isoDateStringSchema.nullable(),
  checkOutAt: isoDateStringSchema.nullable(),
  workedMinutes: z.number().int().nonnegative(),
  status: attendanceStatusSchema,
});
export type KioskResult = z.infer<typeof kioskResultSchema>;

/**
 * The manager-facing view of the link — what the attendance settings screen renders and what
 * `GET /api/v1/attendance/kiosk-link` returns. `token` and `url` are `null` both before the
 * link has ever been created and after it has been revoked; a shop reads `selfCheckInEnabled`
 * off the same response so the screen can explain, in one place, why a valid link might still
 * be refused at the door.
 */
export const attendanceKioskLinkSchema = z.object({
  token: kioskTokenSchema.nullable(),
  url: z.string().nullable(),
  rotatedAt: isoDateStringSchema.nullable(),
  revokedAt: isoDateStringSchema.nullable(),
  selfCheckInEnabled: z.boolean(),
});
export type AttendanceKioskLink = z.infer<typeof attendanceKioskLinkSchema>;
